import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { plotEpisodes } from '@/mocks/plotEpisodes';
import { castMembers } from '@/mocks/castMembers';
import { newsArticles } from '@/mocks/newsArticles';

type SearchBoxProps = {
  tone?: 'light' | 'dark';
  onSubmitted?: () => void;
  autoFocus?: boolean;
};

type Suggestion = {
  key: string;
  label: string;
  meta: string;
  path: string;
};

function buildSuggestions(keyword: string): Suggestion[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];

  const plots: Suggestion[] = plotEpisodes
    .filter((item) => `${item.title}${item.summary}${item.episode}`.toLowerCase().includes(q))
    .slice(0, 3)
    .map((item) => ({
      key: `plot-${item.id}`,
      label: `第${item.episode}集 · ${item.title}`,
      meta: '剧情',
      path: `/detail/plot/${item.id}`,
    }));

  const cast: Suggestion[] = castMembers
    .filter((item) => `${item.name}${item.role}${item.tagline}`.toLowerCase().includes(q))
    .slice(0, 3)
    .map((item) => ({
      key: `cast-${item.id}`,
      label: `${item.name} · 饰 ${item.role}`,
      meta: '演员',
      path: `/detail/cast/${item.id}`,
    }));

  const news: Suggestion[] = newsArticles
    .filter((item) => `${item.title}${item.summary}`.toLowerCase().includes(q))
    .slice(0, 3)
    .map((item) => ({
      key: `news-${item.id}`,
      label: item.title,
      meta: '动态',
      path: `/detail/news/${item.id}`,
    }));

  return [...plots, ...cast, ...news].slice(0, 7);
}

export default function SearchBox({ tone = 'light', onSubmitted, autoFocus = false }: SearchBoxProps) {
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const suggestions = useMemo(() => buildSuggestions(keyword), [keyword]);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const submit = () => {
    const value = keyword.trim();
    if (!value) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(value)}`);
    onSubmitted?.();
  };

  const go = (path: string) => {
    setOpen(false);
    setKeyword('');
    navigate(path);
    onSubmitted?.();
  };

  const isDark = tone === 'dark';

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div
        className={`flex items-center gap-2 rounded-full border px-3 py-2 transition-colors ${
          isDark
            ? 'border-background-50/30 bg-background-50/10 text-background-50 focus-within:border-background-50/70'
            : 'border-background-300 bg-background-50 text-foreground-800 focus-within:border-primary-400'
        }`}
      >
        <span className="flex h-4 w-4 items-center justify-center text-[15px] opacity-70">
          <i className="ri-search-line" />
        </span>
        <input
          value={keyword}
          autoFocus={autoFocus}
          onChange={(event) => {
            setKeyword(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
            if (event.key === 'Escape') setOpen(false);
          }}
          type="text"
          aria-label="站内搜索"
          placeholder="搜索剧情、演员或动态"
          className={`w-full bg-transparent text-sm outline-none ${
            isDark ? 'placeholder:text-background-50/60' : 'placeholder:text-foreground-400'
          }`}
        />
        {keyword && (
          <button
            type="button"
            aria-label="清空搜索"
            onClick={() => setKeyword('')}
            className={`flex h-4 w-4 items-center justify-center text-sm transition-opacity hover:opacity-70 ${
              isDark ? 'text-background-50/80' : 'text-foreground-400'
            }`}
          >
            <i className="ri-close-line" />
          </button>
        )}
      </div>

      {open && keyword.trim() && (
        <div className="absolute left-0 right-0 top-[calc(100%+10px)] z-50 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2 text-foreground-800 animate-pop-in">
          {suggestions.length > 0 ? (
            <ul>
              {suggestions.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => go(item.path)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-primary-100/60"
                  >
                    <span className="shrink-0 rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-900">
                      {item.meta}
                    </span>
                    <span className="line-clamp-1 text-sm">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-3 text-sm text-foreground-500">没有找到相关内容，换个关键词试试</p>
          )}
          <button
            type="button"
            onClick={submit}
            className="mt-1 flex w-full items-center justify-between border-t border-background-200 px-4 pt-2.5 text-xs text-primary-600 transition-colors hover:text-primary-700"
          >
            <span>查看「{keyword.trim()}」的全部结果</span>
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      )}
    </div>
  );
}